import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, Plus, X } from "lucide-react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/constants";

// Poll option builder used in the poll window.
export function PollEditor({ options, multi, onChange }) {
  const setOpt = (i, v) => onChange({ options: options.map((o, j) => (j === i ? v : o)) });
  return (
    <div className="space-y-2" data-testid="announcement-poll-editor">
      {options.map((o, i) => (
        <div key={i} className="group flex h-11 items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] pl-2 pr-3 transition-colors duration-150 focus-within:border-[#00FF66]/60 focus-within:bg-white/[0.05] hover:border-white/20">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white/[0.06] text-[11px] font-black tabular-nums text-white/50 transition-colors group-focus-within:bg-[#00FF66]/15 group-focus-within:text-[#00FF66]">{i + 1}</span>
          <input value={o} maxLength={120} onChange={(e) => setOpt(i, e.target.value)} placeholder={`Option ${i + 1}`} className="h-full flex-1 bg-transparent text-sm text-white outline-none placeholder:text-white/30" data-testid={`announcement-poll-option-${i}`} />
          {options.length > 2 && (
            <button type="button" onClick={() => onChange({ options: options.filter((_, j) => j !== i) })} className="rounded-md p-1 text-white/30 transition-colors hover:bg-white/10 hover:text-[#FF8B94]" aria-label="Remove option" data-testid={`announcement-poll-option-remove-${i}`}><X className="h-3.5 w-3.5" /></button>
          )}
        </div>
      ))}
      {options.length < 10 && (
        <button type="button" onClick={() => onChange({ options: [...options, ""] })} className="flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-dashed border-white/15 text-xs font-bold text-white/50 transition-colors duration-150 hover:border-[#00FF66]/50 hover:bg-[#00FF66]/[0.04] hover:text-[#00FF66]" data-testid="announcement-poll-option-add">
          <Plus className="h-3.5 w-3.5" /> Add option
        </button>
      )}
      <button type="button" role="switch" aria-checked={multi} onClick={() => onChange({ multi: !multi })} className="flex w-full items-center justify-between gap-3 rounded-xl px-1 pt-1.5 text-left" data-testid="announcement-poll-multi">
        <span>
          <span className="block text-xs font-semibold text-white/75">Allow several choices</span>
          <span className="block text-[11px] text-white/35">{multi ? "People can pick more than one option" : "People pick exactly one option"}</span>
        </span>
        <span className={`relative h-5 w-9 shrink-0 rounded-full transition-colors duration-200 ${multi ? "bg-[#00FF66]" : "bg-white/15"}`}>
          <span className={`absolute top-0.5 h-4 w-4 rounded-full shadow transition-transform duration-200 ${multi ? "translate-x-[18px] bg-black" : "translate-x-0.5 bg-white"}`} />
        </span>
      </button>
    </div>
  );
}

// Votable poll with live result bars; voter names only for the poster and admins.
export function PollBlock({ ann, onVoted }) {
  const [poll, setPoll] = useState(ann.poll);
  const [busy, setBusy] = useState(false);
  useEffect(() => setPoll(ann.poll), [ann.poll]);
  const shown = poll;
  if (!shown) return null;
  const mine = shown.my_votes || [];
  const voted = mine.length > 0;
  const notOpen = !!ann.scheduled;
  const closed = !ann.active;
  const showResults = voted || closed || shown.can_see_voters;
  const total = Math.max(1, shown.multi ? shown.voters : shown.options.reduce((s, o) => s + o.count, 0));

  const vote = async (id) => {
    if (closed || busy) return;
    const picks = shown.multi ? (mine.includes(id) ? mine.filter((x) => x !== id) : [...mine, id]) : (mine.includes(id) ? [] : [id]);
    setBusy(true);
    try {
      const { data } = await api.post(`/announcements/${ann.id}/vote`, { option_ids: picks }, { params: { tz_offset: -new Date().getTimezoneOffset() } });
      setPoll(data);
      onVoted?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-4 space-y-1.5" data-testid={`announcement-poll-${ann.id}`}>
      {shown.options.map((o) => {
        const pct = Math.round((o.count / total) * 100);
        const on = mine.includes(o.id);
        return (
          <button key={o.id} type="button" disabled={closed || busy} onClick={() => vote(o.id)}
            className={`relative flex w-full items-center gap-3 overflow-hidden rounded-xl border px-3.5 py-2.5 text-left text-sm transition-colors duration-150 disabled:cursor-default ${on ? "border-[#00FF66]/50" : "border-white/10 hover:border-white/25"}`}
            data-testid={`announcement-poll-vote-${ann.id}-${o.id}`}>
            {showResults && <motion.span className={`absolute inset-y-0 left-0 ${on ? "bg-[#00FF66]/15" : "bg-white/[0.05]"}`} initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }} />}
            <span className={`relative flex h-4 w-4 shrink-0 items-center justify-center border ${shown.multi ? "rounded-[4px]" : "rounded-full"} ${on ? "border-[#00FF66] bg-[#00FF66] text-black" : "border-white/30"}`}>{on && <Check className="h-2.5 w-2.5" strokeWidth={3} />}</span>
            <span className="relative min-w-0 flex-1">
              <span className={`block break-words font-semibold ${on ? "text-white" : "text-white/75"}`}>{o.text}</span>
              {o.voters?.length > 0 && <span className="block truncate text-[10px] text-white/40" data-testid={`announcement-poll-voters-${o.id}`}>{o.voters.join(", ")}</span>}
            </span>
            {showResults && <span className="relative text-xs font-bold tabular-nums text-white/55">{pct}% · {o.count}</span>}
          </button>
        );
      })}
      <p className="pt-1 text-[11px] text-white/35" data-testid={`announcement-poll-summary-${ann.id}`}>
        {shown.voters} {shown.voters === 1 ? "vote" : "votes"} · {shown.multi ? "pick any" : "pick one"}{notOpen ? " · opens when published" : closed ? " · voting closed" : voted ? " · tap again to change" : ""}
        {shown.can_see_voters && shown.voters > 0 && " · names visible to you only"}
      </p>
    </div>
  );
}
